/**
 * Player Dashboard - Home with shareable social stats
 * Designed to feed player ego and encourage social sharing
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Play, MapPin, Trophy, TrendingUp, TrendingDown, 
  Calendar, Video, QrCode, ChevronRight, Share2,
  Flame, Target, Zap, Crown, Loader2, Clock, Star,
  Award, Users, Clapperboard
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Navbar } from '../components/Navbar';
import { useAuthStore, useSessionStore } from '../store';
import { playerAPI } from '../services/api';
import { toast } from 'sonner';

// Default empty stats
const EMPTY_STATS = {
  totalGamesMonth: 0,
  totalGamesLastMonth: 0,
  totalGamesWeek: 0,
  totalGamesLastWeek: 0,
  totalVideos: 0,
  totalVideosLastMonth: 0,
  totalSecondsRecorded: 0,
  totalSecondsLastMonth: 0,
  totalPlayTime: 0,
  averageSessionTime: 0,
  favoriteClub: null,
  locations: [],
  recentGames: [],
  hasData: false,
  playerRank: null, // e.g., "Top 10%"
  streakDays: 0,
};

// Big hero stat for social sharing
const HeroStat = ({ icon: Icon, value, label, subLabel, gradient, iconBg }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.9 }}
    animate={{ opacity: 1, scale: 1 }}
    className={`relative overflow-hidden rounded-2xl p-6 ${gradient} border border-white/10`}
  >
    {/* Background glow effect */}
    <div className="absolute -top-12 -right-12 w-32 h-32 bg-[#BBFF00]/20 rounded-full blur-3xl" />
    
    <div className={`w-14 h-14 rounded-xl ${iconBg} flex items-center justify-center mb-4`}>
      <Icon className="w-7 h-7 text-[#070A0F]" />
    </div>
    
    <p className="text-5xl font-display font-black text-white tracking-tight">{value}</p>
    <p className="text-lg font-semibold text-slate-200 mt-1">{label}</p>
    {subLabel && (
      <p className="text-sm text-slate-400 mt-1">{subLabel}</p>
    )}
  </motion.div>
);

// Shareable stat card
const StatCard = ({ icon: Icon, label, value, subValue, trend, trendValue, highlight = false }) => {
  const isPositive = trend === 'up';
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`rounded-xl p-4 border ${
        highlight 
          ? 'bg-gradient-to-br from-[#BBFF00]/10 to-[#0D121D] border-[#BBFF00]/30' 
          : 'bg-[#131B2A] border-white/10'
      }`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
          highlight ? 'bg-[#BBFF00]/20 text-[#BBFF00]' : 'bg-white/5 text-slate-400'
        }`}>
          <Icon className="w-5 h-5" />
        </div>
        {trend && (
          <div className={`flex items-center gap-1 text-xs font-medium ${
            isPositive ? 'text-emerald-400' : 'text-rose-400'
          }`}>
            {isPositive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            <span>{trendValue}</span>
          </div>
        )}
      </div>
      <p className={`text-3xl font-display font-bold ${highlight ? 'text-[#BBFF00]' : 'text-white'}`}>
        {value}
      </p>
      <p className="text-sm text-slate-400">{label}</p>
      {subValue && (
        <p className="text-xs text-slate-500 mt-1">{subValue}</p>
      )}
    </motion.div>
  );
};

// Achievement badge
const AchievementBadge = ({ icon: Icon, label, unlocked = false }) => (
  <div className={`flex flex-col items-center p-3 rounded-xl border ${
    unlocked 
      ? 'bg-[#BBFF00]/10 border-[#BBFF00]/30 text-[#BBFF00]' 
      : 'bg-white/5 border-white/10 text-slate-600'
  }`}>
    <Icon className={`w-6 h-6 mb-1 ${unlocked ? '' : 'opacity-40'}`} />
    <span className="text-xs text-center">{label}</span>
  </div>
);

// Club visit item
const ClubItem = ({ location, index, isTop = false }) => (
  <motion.div
    initial={{ opacity: 0, x: -20 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ delay: index * 0.1 }}
    className={`flex items-center gap-3 p-3 rounded-xl ${
      isTop ? 'bg-[#BBFF00]/10 border border-[#BBFF00]/30' : 'bg-white/5'
    }`}
  >
    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
      isTop ? 'bg-[#BBFF00] text-[#070A0F]' : 'bg-white/10 text-slate-400'
    }`}>
      {isTop ? <Star className="w-5 h-5" /> : index + 1}
    </div>
    <div className="flex-1 min-w-0">
      <p className={`font-semibold truncate ${isTop ? 'text-[#BBFF00]' : 'text-white'}`}>
        {location.name}
      </p>
      <p className="text-xs text-slate-500">{location.games} partidos • {location.videos || 0} videos</p>
    </div>
    {isTop && (
      <span className="text-xs bg-[#BBFF00]/20 text-[#BBFF00] px-2 py-1 rounded-full font-medium">
        Favorito
      </span>
    )}
  </motion.div>
);

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { club, court } = useSessionStore();
  const [stats, setStats] = useState(EMPTY_STATS);
  const [loading, setLoading] = useState(true);
  
  const hasActiveSession = !!(club && court);
  
  // Fetch real player stats
  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      const response = await playerAPI.getStats();
      setStats({
        ...EMPTY_STATS,
        ...response.data,
        // Ensure video stats exist
        totalVideos: response.data.totalVideos || response.data.recentGames?.length || 0,
        totalSecondsRecorded: response.data.totalSecondsRecorded || 
          (response.data.recentGames?.reduce((sum, g) => sum + (g.duration || 0), 0) || 0),
      });
    } catch (err) {
      console.error('Error fetching player stats:', err);
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    fetchStats();
  }, [fetchStats]);
  
  // Format seconds to readable time
  const formatSeconds = (seconds) => {
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${mins}m`;
  };
  
  // Calculate trends
  const videosTrend = stats.totalVideos >= (stats.totalVideosLastMonth || 0) ? 'up' : 'down';
  const videosTrendValue = stats.totalVideosLastMonth > 0 
    ? `${Math.abs(stats.totalVideos - stats.totalVideosLastMonth)} vs mes ant.`
    : stats.totalVideos > 0 ? '+' + stats.totalVideos : '—';
  
  const gamesTrend = stats.totalGamesMonth >= stats.totalGamesLastMonth ? 'up' : 'down';
  const gamesTrendValue = `${Math.abs(stats.totalGamesMonth - stats.totalGamesLastMonth)} vs mes ant.`;
  
  // Share stats function
  const handleShareStats = async () => {
    const shareText = `🎾 Mis stats de Clipper:\n\n` +
      `📹 ${stats.totalVideos} replays generados\n` +
      `⏱️ ${formatSeconds(stats.totalSecondsRecorded)} de video\n` +
      `🏆 ${stats.totalGamesMonth} partidos este mes\n` +
      `📍 ${stats.locations.length} clubes visitados\n\n` +
      `¡Descarga Clipper y graba tus mejores jugadas! 🔥\n` +
      `https://sportsreplay.app`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Mis stats de Clipper',
          text: shareText,
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          navigator.clipboard.writeText(shareText);
          toast.success('Stats copiados al portapapeles');
        }
      }
    } else {
      navigator.clipboard.writeText(shareText);
      toast.success('Stats copiados al portapapeles');
    }
  };
  
  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#070A0F] pb-24">
        <Navbar />
        <div className="max-w-4xl mx-auto p-4 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#BBFF00] mb-4" />
            <p className="text-slate-400">Cargando tus estadísticas...</p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-[#070A0F] pb-24">
      <Navbar />
      
      <div className="max-w-4xl mx-auto p-4">
        {/* Welcome header with share button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <div className="flex items-center gap-3 mb-1">
            <h2 className="font-display font-bold text-2xl md:text-3xl text-white">
              {user?.first_name ? `¡Qué onda, ${user.first_name}!` : '¡Bienvenido!'} 🔥
            </h2>
            <button
              onClick={handleShareStats}
              className="p-1.5 rounded-full border border-[#BBFF00]/30 text-[#BBFF00]/70 hover:bg-[#BBFF00]/10 hover:text-[#BBFF00] hover:border-[#BBFF00]/50 transition-all"
              title="Compartir estadísticas"
            >
              <Share2 className="w-3.5 h-3.5" />
            </button>
          </div>
          <p className="text-slate-400">
            Tus estadísticas de jugador
          </p>
        </motion.div>
        
        {/* Email verification banner - only show if not verified */}
        {user && !user.email_verified && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 rounded-xl bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30"
          >
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-amber-500/20">
                <Zap className="w-5 h-5 text-amber-400" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-amber-200 mb-1">
                  ¡Verifica tu correo y guarda tus videos 3 días!
                </h3>
                <p className="text-sm text-slate-400 mb-3">
                  Sin verificar, tus replays se borran en 24 horas. Con email verificado, se guardan 72 horas.
                </p>
                <Button
                  size="sm"
                  onClick={() => navigate('/profile')}
                  className="bg-amber-500 hover:bg-amber-600 text-black font-medium"
                >
                  Verificar ahora
                </Button>
              </div>
            </div>
          </motion.div>
        )}
        
        {/* Hero Stats - Main shareable numbers */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <HeroStat
            icon={Clapperboard}
            value={stats.totalVideos}
            label="Replays generados"
            subLabel="Videos de tus mejores jugadas"
            gradient="bg-gradient-to-br from-[#1a2332] to-[#0D121D]"
            iconBg="bg-[#BBFF00]"
          />
          
          <HeroStat
            icon={Clock}
            value={formatSeconds(stats.totalSecondsRecorded)}
            label="Segundos de video"
            subLabel="Contenido épico capturado"
            gradient="bg-gradient-to-br from-[#1a2332] to-[#0D121D]"
            iconBg="bg-[#BBFF00]"
          />
        </div>
        
        {/* Secondary Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <StatCard
            icon={Trophy}
            label="Partidos este mes"
            value={stats.totalGamesMonth}
            trend={gamesTrend}
            trendValue={gamesTrendValue}
            highlight={stats.totalGamesMonth > 5}
          />
          
          <StatCard
            icon={MapPin}
            label="Clubes visitados"
            value={stats.locations.length}
            subValue="diferentes ubicaciones"
          />
          
          <StatCard
            icon={Calendar}
            label="Esta semana"
            value={stats.totalGamesWeek}
            subValue="partidos jugados"
          />
          
          <StatCard
            icon={Flame}
            label="Racha actual"
            value={stats.streakDays || 0}
            subValue="días consecutivos"
            highlight={stats.streakDays >= 3}
          />
        </div>
        
        {/* Achievements Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-[#131B2A] rounded-2xl p-4 border border-white/10 mb-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-bold text-lg text-white flex items-center gap-2">
              <Award className="w-5 h-5 text-[#BBFF00]" />
              Logros
            </h3>
            <span className="text-xs text-slate-500">
              {[
                stats.totalVideos >= 1,
                stats.totalVideos >= 10,
                stats.totalVideos >= 50,
                stats.locations.length >= 3,
                stats.totalGamesMonth >= 10,
                stats.streakDays >= 7
              ].filter(Boolean).length} / 6 desbloqueados
            </span>
          </div>
          
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
            <AchievementBadge 
              icon={Video} 
              label="Primer Replay" 
              unlocked={stats.totalVideos >= 1} 
            />
            <AchievementBadge 
              icon={Star} 
              label="10 Replays" 
              unlocked={stats.totalVideos >= 10} 
            />
            <AchievementBadge 
              icon={Crown} 
              label="50 Replays" 
              unlocked={stats.totalVideos >= 50} 
            />
            <AchievementBadge 
              icon={MapPin} 
              label="Explorador" 
              unlocked={stats.locations.length >= 3} 
            />
            <AchievementBadge 
              icon={Trophy} 
              label="Pro Player" 
              unlocked={stats.totalGamesMonth >= 10} 
            />
            <AchievementBadge 
              icon={Flame} 
              label="En Racha" 
              unlocked={stats.streakDays >= 7} 
            />
          </div>
        </motion.div>
        
        {/* Clubs Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="bg-[#131B2A] rounded-2xl p-4 border border-white/10 mb-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-bold text-lg text-white flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[#BBFF00]" />
              Tus Clubes
            </h3>
            <span className="text-xs bg-white/5 text-slate-400 px-2 py-1 rounded-full">
              {stats.locations.length} clubes
            </span>
          </div>
          
          <div className="space-y-2">
            {stats.locations.length > 0 ? (
              stats.locations.map((location, index) => (
                <ClubItem 
                  key={location.name} 
                  location={location} 
                  index={index}
                  isTop={index === 0 && stats.locations.length > 1}
                />
              ))
            ) : (
              <div className="py-8 text-center">
                <MapPin className="w-10 h-10 mx-auto mb-3 text-slate-600" />
                <p className="text-slate-400 font-medium">Aún no has visitado clubes</p>
                <p className="text-sm text-slate-500 mt-1">
                  Escanea el QR en una cancha para comenzar
                </p>
              </div>
            )}
          </div>
        </motion.div>
        
        {/* Premium Banner */}
        {user?.role !== 'player_premium' && user?.plan !== 'premium' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mb-6"
          >
            <div 
              onClick={() => toast.info('Función disponible próximamente')}
              className="rounded-2xl p-4 cursor-pointer hover:scale-[1.01] transition-transform bg-gradient-to-r from-amber-500/10 via-yellow-500/10 to-orange-500/10 border border-yellow-500/30"
            >
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center shrink-0 shadow-lg shadow-yellow-500/20">
                  <Crown className="w-8 h-8 text-[#070A0F]" />
                </div>
                <div className="flex-1">
                  <h3 className="font-display font-bold text-lg text-yellow-400">¡Hazte Premium!</h3>
                  <p className="text-sm text-slate-400">
                    Replays ilimitados, sin marca de agua + <span className="text-yellow-400 font-medium">Botón Shelly GRATIS</span>
                  </p>
                </div>
                <div className="text-right shrink-0 hidden sm:block">
                  <p className="text-2xl font-bold text-white">$1,000</p>
                  <p className="text-xs text-slate-500">MXN / 6 meses</p>
                </div>
                <ChevronRight className="w-5 h-5 text-yellow-400 shrink-0" />
              </div>
            </div>
          </motion.div>
        )}

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="bg-[#131B2A] rounded-2xl p-4 border border-white/10"
        >
          <h3 className="font-display font-bold text-lg text-white mb-4">Acciones rápidas</h3>
          
          <div className="grid grid-cols-3 gap-3">
            <Button
              className="h-16 flex-col gap-1 bg-[#BBFF00] hover:bg-[#A2DC00] text-[#070A0F] font-bold shadow-[0_0_20px_rgba(187,255,0,0.3)]"
              onClick={() => navigate('/cancha')}
            >
              {hasActiveSession ? (
                <>
                  <Zap className="w-6 h-6" />
                  <span className="text-xs">Ir a la cancha</span>
                </>
              ) : (
                <>
                  <QrCode className="w-6 h-6" />
                  <span className="text-xs">Escanear QR</span>
                </>
              )}
            </Button>
            
            <Button
              variant="secondary"
              className="h-16 flex-col gap-1 bg-white/5 hover:bg-white/10 text-white border border-white/10"
              onClick={() => navigate('/replays')}
            >
              <Play className="w-6 h-6" />
              <span className="text-xs">Mis Replays</span>
            </Button>
            
            <Button
              variant="outline"
              className="h-16 flex-col gap-1 border-[#BBFF00]/30 text-[#BBFF00] hover:bg-[#BBFF00]/10"
              onClick={handleShareStats}
            >
              <Share2 className="w-6 h-6" />
              <span className="text-xs">Compartir</span>
            </Button>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
